import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import Layout, { PageHeader } from "../components/Layout";
import api, { formatApiError, hasWrite, isApprover } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { askReason, useApprovalLocks, LockNotice } from "../components/ApprovalLock";
import { InvoiceFilters, EMPTY_FILTERS, filtersToParams } from "../components/invoices/InvoiceFilters";
import { InvoiceTable } from "../components/invoices/InvoiceTable";
import { BulkActionBar } from "../components/invoices/BulkActionBar";
import { InvoiceUploadBar, InvoiceUploadHelp } from "../components/invoices/InvoiceUploadBar";
import { PaymentDialog } from "../components/invoices/PaymentDialog";
import { CreditNoteDialog } from "../components/invoices/CreditNoteDialog";
import { EditInvoiceDialog } from "../components/invoices/EditInvoiceDialog";
import { PaymentModeDialog } from "../components/invoices/PaymentModeDialog";
import { cancelInvoice, exportInvoicesCsv, openInvoiceReceipt } from "../components/invoices/invoiceActions";

const useInvoiceList = () => {
  const [invoices, setInvoices] = useState([]);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [selected, setSelected] = useState(new Set());
  const load = useCallback(async () => {
    setInvoices((await api.get("/invoices", { params: filtersToParams(filters) })).data);
    setSelected(new Set());
  }, [filters]);
  useEffect(() => {
    const t = setTimeout(load, 250);
    return () => clearTimeout(t);
  }, [load]);
  return { invoices, filters, setFilters, selected, setSelected, load };
};

const useFilterOptions = () => {
  const [partyOpts, setPartyOpts] = useState([]);
  const [accountOpts, setAccountOpts] = useState([]);
  useEffect(() => {
    api.get("/parties").then((r) => setPartyOpts((r.data || []).map((p) => p.name))).catch((e) => console.error(e));
    Promise.all([api.get("/credit-accounts"), api.get("/invoices/account-names")])
      .then(([r, n]) => setAccountOpts([...new Set([...(r.data || []).map((a) => a.account || a.name), ...(n.data || [])].filter(Boolean))].sort()))
      .catch((e) => console.error(e));
  }, []);
  return { partyOpts, accountOpts };
};

const cancelPrompt = (user, inv) => window.prompt(`Cancel invoice ${inv.invoice_no}? Amount becomes ₹0 and all cash/bank payments & receipts against it are DELETED from the Cash Book.\n\nReason${isApprover(user) ? " (optional)" : " (required — admin must approve)"}:`, "");

export default function Invoices() {
  const { user } = useAuth();
  const canWrite = hasWrite(user);
  const [locks, reloadLocks] = useApprovalLocks();
  const [lockInfo, setLockInfo] = useState(null);
  const [skippedReport, setSkippedReport] = useState(null);
  const [dialog, setDialog] = useState({ kind: null, inv: null });
  const { invoices, filters, setFilters, selected, setSelected, load } = useInvoiceList();
  const { partyOpts, accountOpts } = useFilterOptions();

  const openDialog = (kind) => (inv) => setDialog({ kind, inv });
  const closeDialog = () => setDialog({ kind: null, inv: null });
  const dialogInv = (kind) => (dialog.kind === kind ? dialog.inv : null);

  const toggleSel = (id) => setSelected((s) => {
    const n = new Set(s);
    n.has(id) ? n.delete(id) : n.add(id);
    return n;
  });
  const allSelected = invoices.length > 0 && invoices.every((i) => selected.has(i.id));
  const toggleAll = () => setSelected(allSelected ? new Set() : new Set(invoices.map((i) => i.id)));

  const exportSelected = () => {
    const picked = invoices.filter((i) => selected.has(i.id));
    if (picked.length === 0) return toast.error("Select at least one invoice");
    exportInvoicesCsv(picked);
  };

  const cancelSelected = async () => {
    const picked = invoices.filter((i) => selected.has(i.id) && i.doc_status !== "cancelled");
    if (picked.length === 0) return toast.error("Select active invoices to cancel");
    const reason = window.prompt(`Cancel ${picked.length} invoice(s)? Amounts become ₹0 and all cash/bank payments & receipts against them are DELETED from the Cash Book.\n\nOptional reason:`, "");
    if (reason === null) return;
    try {
      let n = 0;
      for (const inv of picked) if (await cancelInvoice(inv, reason, reloadLocks)) n++;
      toast.success(`${n} invoice(s) cancelled`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); load(); }
  };

  const cancel = async (inv) => {
    const reason = cancelPrompt(user, inv);
    if (reason === null) return;
    if (!isApprover(user) && !reason.trim()) return toast.error("A reason is required");
    try {
      if (await cancelInvoice(inv, reason, reloadLocks)) toast.success(`Invoice ${inv.invoice_no} cancelled`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const del = async (inv) => {
    const msg = isApprover(user)
      ? `Delete invoice ${inv.invoice_no}? Its recorded payments will be reversed.`
      : `Request deletion of invoice ${inv.invoice_no}? An admin must approve it.`;
    if (!window.confirm(msg)) return;
    const reason = askReason(user, "deleting", `invoice ${inv.invoice_no}`);
    if (reason === null) return;
    try {
      const res = await api.delete(`/invoices/${inv.id}`, { params: { reason } });
      if (res.data?.status === "pending") { toast.info(res.data.message || "Sent for approval"); reloadLocks(); }
      else toast.success("Invoice deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const act = {
    guard: (inv, fn) => () => (locks[inv.id] ? setLockInfo(locks[inv.id]) : fn(inv)),
    receipt: openInvoiceReceipt, payMode: openDialog("pm"), edit: openDialog("edit"),
    pay: openDialog("pay"), creditNote: openDialog("cn"), cancel, del,
  };
  const dlgProps = { onClose: closeDialog, onChanged: load, user, reloadLocks };

  return (
    <Layout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-7xl mx-auto">
        <PageHeader title="Invoices" subtitle="Track each bill — amount, paid, and running balance.">
          {canWrite && <InvoiceUploadBar skippedReport={skippedReport} setSkippedReport={setSkippedReport} onImported={load} />}
        </PageHeader>
        {canWrite && <InvoiceUploadHelp />}
        <InvoiceFilters filters={filters} setFilters={setFilters} partyOpts={partyOpts} accountOpts={accountOpts} />
        <BulkActionBar count={selected.size} canWrite={canWrite} onExport={exportSelected} onCancel={cancelSelected} onClear={() => setSelected(new Set())} />
        <InvoiceTable invoices={invoices} selected={selected} toggleSel={toggleSel} allSelected={allSelected} toggleAll={toggleAll}
          locks={locks} setLockInfo={setLockInfo} canWrite={canWrite} act={act} />
        <PaymentDialog invoice={dialogInv("pay")} canWrite={canWrite} {...dlgProps} />
        <CreditNoteDialog invoice={dialogInv("cn")} canWrite={canWrite} {...dlgProps} />
        <EditInvoiceDialog invoice={dialogInv("edit")} partyOpts={partyOpts} accountOpts={accountOpts} {...dlgProps} />
        <PaymentModeDialog invoice={dialogInv("pm")} {...dlgProps} />
      </div>
      <LockNotice info={lockInfo} onClose={() => setLockInfo(null)} />
    </Layout>
  );
}
